import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField
} from '@mui/material';
import {
    Add as AddIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { adminApiClient } from '../utils/adminApiClient';
import { RoleCard } from '../components/RoleCard';

interface Role {
    id: string;
    ident: string;
    name: string;
}


export const RoleManagement: React.FC = () => {
    const [systemRoles, setSystemRoles] = useState<Role[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedRole, setSelectedRole] = useState<Role | null>(null);
    const [newRole, setNewRole] = useState({
        name: '',
        ident: ''
    });
    const { enqueueSnackbar } = useSnackbar();

    const loadSystemRoles = async () => {
        try {
            setLoading(true);
            const roles = await adminApiClient.getRoles();
            setSystemRoles(roles);
        } catch (error) {
            console.error('Error loading system roles:', error);
            setError('Fehler beim Laden der System-Rollen');
            enqueueSnackbar('Fehler beim Laden der System-Rollen', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadSystemRoles();
    }, []);

    const handleCreateRole = async () => {
        try {
            await adminApiClient.createRole(newRole);
            await loadSystemRoles();
            setCreateDialogOpen(false);
            setNewRole({ name: '', ident: '' });
            enqueueSnackbar('Rolle erfolgreich erstellt', { variant: 'success' });
        } catch (error) {
            console.error('Error creating role:', error);
            enqueueSnackbar('Fehler beim Erstellen der Rolle', { variant: 'error' });
        }
    };

    const handleDeleteRole = async () => {
        if (!selectedRole) return;

        try {
            await adminApiClient.deleteRole(selectedRole.ident);
            await loadSystemRoles();
            setDeleteDialogOpen(false);
            setSelectedRole(null);
            enqueueSnackbar('Rolle erfolgreich gelöscht', { variant: 'success' });
        } catch (error) {
            console.error('Error deleting role:', error);
            enqueueSnackbar('Fehler beim Löschen der Rolle', { variant: 'error' });
        }
    };

    const handleRefresh = () => {
        loadSystemRoles();
    };

    const handleEditRole = (_role: Role) => {
        // TODO: Implement role editing
        enqueueSnackbar('Rollen-Bearbeitung wird in einer zukünftigen Version implementiert', { variant: 'info' });
    };

    const handleDeleteRoleClick = (role: Role) => {
        setSelectedRole(role);
        setDeleteDialogOpen(true);
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1">
                    Rollenverwaltung
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={handleRefresh}
                        disabled={loading}
                    >
                        Aktualisieren
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateDialogOpen(true)}
                    >
                        Neue Rolle
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Typography variant="h6" sx={{ mb: 1 }}>
                System-Rollen
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                System-Rollen sind globale Rollen, die für die gesamte Anwendung gelten. Client-Rollen können über die Client-Verwaltung verwaltet werden.
            </Typography>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : systemRoles.length === 0 ? (
                <Alert severity="info">
                    Keine System-Rollen gefunden.
                </Alert>
            ) : (
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: 2
                }}>
                    {systemRoles.map(role => (
                        <RoleCard
                            key={role.id}
                            role={{ ...role, clientId: '' }}
                            isSystemRole={true}
                            onEdit={handleEditRole}
                            onDelete={handleDeleteRoleClick}
                        />
                    ))}
                </Box>
            )}

            {/* Create Role Dialog */}
            <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Neue Rolle erstellen</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <TextField
                            fullWidth
                            label="Rollen-Name"
                            value={newRole.name}
                            onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                            sx={{ mb: 2 }}
                        />
                        
                        <TextField
                            fullWidth
                            label="Rollen-Identifikator"
                            value={newRole.ident}
                            onChange={(e) => setNewRole({ ...newRole, ident: e.target.value })}
                            helperText="Eindeutiger Identifikator für die Rolle (z.B. 'admin', 'user', 'moderator')"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateDialogOpen(false)}>Abbrechen</Button>
                    <Button 
                        onClick={handleCreateRole} 
                        variant="contained"
                        disabled={!newRole.name || !newRole.ident}
                    >
                        Erstellen
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Role Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Rolle löschen</DialogTitle>
                <DialogContent>
                    <Typography>
                        Möchten Sie die Rolle "{selectedRole?.name}" wirklich löschen?
                        Diese Aktion kann nicht rückgängig gemacht werden.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
                    <Button onClick={handleDeleteRole} color="error" variant="contained">
                        Löschen
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
